<script setup lang="ts">
import {
  COMMON_RESET_PAGE_ON,
  commonFilterFields,
  type Creator,
  CreatorStatusFilterEnum,
  type CreatorsV2Filter,
  enumField,
  numberEntityArrayField,
  PaginationComponent,
  stringEntityField,
  useQueryFilter,
} from '@metapic-apps/shared'
import { computed, reactive, watch } from 'vue'

import { useFileDownload, useGetCreators } from '@/api'
import DownloadIcon from '@/assets/icons/download.svg?component'
import CreatorListItem from '@/components/CreatorListItem/CreatorListItem.vue'
import CreatorsBulkActionPopup from '@/components/CreatorsBulkActionPopup/CreatorsBulkActionPopup.vue'
import { FilterComponent } from '@/components/Filter'
import HeaderComponent from '@/components/Navigation/HeaderComponent.vue'
import { buildCreatorsExportParams } from '@/lib/adapters/creators.adapter'
import { MAX_SELECTED_CREATORS } from '@/lib/constants'
import { notification } from '@/lib/helpers'
import { config } from '@/lib/helpers/config.helpers'
import { useUserStore } from '@/stores/user'
import { BoxComponent, ButtonComponent, FlexComponent, LoadingComponent, PageWrapperComponent, TypographyComponent } from '@/ui-library'

const userStore = useUserStore()
const { filter, reset } = useQueryFilter<CreatorsV2Filter>(
  {
    page: commonFilterFields.page,
    query: commonFilterFields.query,
    advertiserGroup: commonFilterFields.advertiserGroup,
    client: commonFilterFields.client,
    status: enumField('status', Object.values(CreatorStatusFilterEnum)),
    segment: stringEntityField<NonNullable<CreatorsV2Filter['segment']>>({
      idKey: 'segmentId',
      labelKey: 'segmentName',
      entityLabelKey: 'name',
    }),
    userTags: numberEntityArrayField('userTagIds', 'userTagNames'),
  },
  {
    page: 1,
    advertiserGroup: {
      id: userStore.user.storeGroupId,
      name: '',
    },
  },
  { resetPageOn: [...COMMON_RESET_PAGE_ON, 'segment', 'userTags'], cleared: { page: 1 } },
)
const { data, isLoading, error, totalPages, refetch } = useGetCreators(filter)
const { downloadFile, isLoading: isExportLoading, error: exportError } = useFileDownload()
const selectedCreators = reactive<Record<number, Creator>>({})

const selectedCreatorsCount = computed(() => Object.keys(selectedCreators).length)

// `/v2/users` only distinguishes these three states. "Validated" / "Not Validated" used to be
// offered too, but they resolved to the same queries as accepted / undecided.
const statusOptions = [
  { label: 'Accepted', value: CreatorStatusFilterEnum.Accepted },
  { label: 'Suspended', value: CreatorStatusFilterEnum.Suspended },
  { label: 'Undecided', value: CreatorStatusFilterEnum.Undecided },
]

const exportLinkUrl = computed(
  () => `${config.metapicApiUrl}/v2/users?${buildCreatorsExportParams({ ...filter.value, format: 'xlsx' }).toString()}`,
)

const handleExport = async () => {
  await downloadFile(exportLinkUrl.value, `creators-export-${new Date().toISOString().split('T')[0]}.xlsx`)
  notification.success('Export completed successfully')
}

const clearSelectedCreators = () => {
  Object.keys(selectedCreators).forEach((key) => {
    delete selectedCreators[Number(key)]
  })
}

const toggleCreatorSelection = (creator: Creator) => {
  if (creator.id in selectedCreators) {
    delete selectedCreators[creator.id]
  } else if (selectedCreatorsCount.value < MAX_SELECTED_CREATORS) {
    selectedCreators[creator.id] = creator
  }
}

const handleBulkActionCompleted = async ({ action, changedAmount }: { action: 'approve' | 'ban'; changedAmount: number }) => {
  notification.success(
    action === 'approve' ? `${changedAmount} creators accepted successfully` : `${changedAmount} creators suspended successfully`,
  )

  clearSelectedCreators()
  await refetch()
}

watch(
  () => exportError.value,
  () => {
    if (exportError.value) {
      notification.error('Failed to export creators list')
    }
  },
)
</script>
<template>
  <HeaderComponent title="Creators">
    <ButtonComponent variant="transparent" :is-loading="isExportLoading" @click="handleExport">
      <template #iconLeft>
        <el-icon :size="18">
          <DownloadIcon class="creators-view__download-icon" />
        </el-icon>
      </template>
      Export list
    </ButtonComponent>
  </HeaderComponent>
  <PageWrapperComponent>
    <BoxComponent :mb="32">
      <FilterComponent
        v-model="filter"
        placeholder="Search creators"
        :status-options="statusOptions"
        show-client-selector
        show-status-selector
        show-segment-selector
        show-user-tag-selector
        @clear="reset"
      />
    </BoxComponent>
    <BoxComponent v-if="error" :mb="24">
      <el-alert :title="error.message" type="error" />
    </BoxComponent>
    <LoadingComponent v-if="isLoading" :size="64" />
    <FlexComponent v-if="!isLoading && data && data?.length === 0" justify="center">
      <TypographyComponent variant="body-large"> There are no creators that match your filters </TypographyComponent>
    </FlexComponent>
    <FlexComponent v-if="data && !isLoading" direction="column" :gap="16">
      <CreatorListItem
        v-for="creator in data"
        :key="creator.id"
        :creator="creator"
        :is-selected="creator.id in selectedCreators"
        @toggle-select="toggleCreatorSelection"
      />
    </FlexComponent>
    <BoxComponent v-if="totalPages > 0 && data && data?.length > 0" :pt="32" :pb="32">
      <PaginationComponent v-model:current-page="filter.page" :total-pages="totalPages" />
    </BoxComponent>
    <CreatorsBulkActionPopup
      v-if="selectedCreatorsCount > 0"
      :selected-creators="selectedCreators"
      :is-limit-reached="selectedCreatorsCount >= MAX_SELECTED_CREATORS"
      @close="clearSelectedCreators"
      @completed="handleBulkActionCompleted"
    />
  </PageWrapperComponent>
</template>
<style lang="scss" scoped>
:deep(.creators-view__download-icon) {
  path {
    fill: var(--inverted);
  }
}
</style>
